import React, { useEffect, useMemo, useState } from "react";
import { PageTitle } from "components/common/Text";
import { useCSVReader } from "react-papaparse";
import ErrorIcon from "@mui/icons-material/Error";
import DownloadDoneIcon from "@mui/icons-material/DownloadDone";
import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
} from "@mui/material";
import { TableBodyCell, TableHeaderCell } from "components/table/table.style";
import { CommonText } from "components/common/text/text.component";
import { PrimaryButton, ButtonText } from "components/common/Buttons";
import {
  getInventoryDevice,
  InventoryStatusTypes,
  InventoryValueTypes,
  INVETNORY_COLUMNS,
  postInventoryData,
} from "./inventory.api";
import style from "./inventory.module.css";
import { inventoryResponseData } from "./inventory.depot";
import TableFilterNew from "components/table/TableFilterNew.component";
import { Spinner } from "components/common/spinner/spinner.component";
import CustomTable from "components/table/table.component";
import { Popup } from "components/common/popup/popup.component";
import { showError, showWarning } from "utils/notifications";

interface ResultQtyTypes {
  successQty: number;
  existsQty: number;
}

const InventoryPage = (): JSX.Element => {
  const { CSVReader } = useCSVReader();

  // below goes on table
  const [inventoryStatus, setInventoryStatus] = useState<InventoryStatusTypes[]>([]);
  const [isUploadDisabled, setIsUploadDisabled] = useState<boolean>(false);
  const [devicesProcessed, setDevicesProcessed] = useState<number>(0);
  const [resultsQty, setResultsQty] = useState<ResultQtyTypes>({
    successQty: 0,
    existsQty: 0,
  });

  const [page, setPage] = React.useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(10);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [deviceData, setDeviceData] = useState<InventoryValueTypes[]>([]);

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    const inventoryDepotData = inventoryResponseData.observable.subscribe({
      next: (data: InventoryStatusTypes[]) => {
        setInventoryStatus(data);
      },
    });
    return () => inventoryDepotData.unsubscribe();
  }, []);

  useEffect(() => {
    let successCount = 0;
    let existsCount = 0;

    inventoryStatus.forEach((value: InventoryStatusTypes) => {
      if (value.uploadStatus === "SerialNumber added") {
        successCount++;
      }
      if (value.uploadStatus === "Rejected, SerialNumber already exists") {
        existsCount++;
      }

      setResultsQty({ successQty: successCount, existsQty: existsCount });
    });
  }, [inventoryStatus]);

  const postInventory = async (inventory: InventoryValueTypes[]): Promise<void> => {
    setIsUploadDisabled(true);
    try {
      // Per-row .catch keeps Promise.all from short-circuiting; interceptor toasts on each failure.
      await Promise.all(inventory.map((data) => postInventoryData(data).catch(() => {})));
    } finally {
      setIsUploadDisabled(false);
    }
  };

  const getResultStats = (acceptedFile: any) => {
    if (!acceptedFile) return;
    if (devicesProcessed === 0) return null;

    return (
      <div>
        <div>
          <CommonText>
            Results: <br />
            {acceptedFile.name} was successfully uploaded! <br />
          </CommonText>
          {resultsQty.successQty ? (
            <CommonText>
              {resultsQty.successQty} of {devicesProcessed} devices were created
            </CommonText>
          ) : (
            <CommonText>
              {resultsQty.existsQty} of the {devicesProcessed} devices already exist in the database
            </CommonText>
          )}
        </div>
      </div>
    );
  };

  const getStatusIcon = (status: string) => {
    if (!status.includes("Rejected")) {
      return <DownloadDoneIcon color="success" />;
    }
    return <ErrorIcon color="warning" />;
  };

  const handleNullData = () => {
    showWarning("Results not found...");
    setDeviceData([]);
    return setIsLoading(false);
  };

  const onSearch = async (column: string, columnType: string, searchContent: string) => {
    try {
      setIsLoading(true);

      const filteredContent = searchContent.replace(/\s+/g, "");

      const param: Record<string, string> = {};
      param[column] = filteredContent;

      // serialNumber
      if (column === "serialNumber") {
        const response = await getInventoryDevice(param);

        setIsLoading(false);

        if (response === null) {
          return handleNullData();
        }
        return setDeviceData(response);
      }
      // MAC
      if (column === "mac") {
        const response = await getInventoryDevice(param);

        setIsLoading(false);

        if (response === null) {
          return handleNullData();
        }

        return setDeviceData(response);
      }

      const response = await getInventoryDevice(param);
      if (response === null) {
        return handleNullData();
      }

      return setDeviceData(response);
    } catch (error: any) {
      console.warn("error", error);
    } finally {
      setIsLoading(false);
    }
  };

  const filterColumns = useMemo(() => INVETNORY_COLUMNS.filter(({ filter }) => filter), []);

  return (
    <>
      <CSVReader
        onUploadAccepted={(results: any) => {
          const allRows: string[][] = results?.data ?? [];
          const dataRows = allRows.slice(1).filter((row) => row && row.length > 1);

          setDevicesProcessed(dataRows.length);

          if (dataRows.length === 0) {
            showError(
              "No valid device rows found in the CSV. Expected comma-separated columns in order: Serial Number, IMEI, MAC.",
            );
            setIsUploadDisabled(false);
            return;
          }

          const newArr: InventoryValueTypes[] = [];
          dataRows.forEach((data: string[]) => {
            const [deviceSn, deviceImei, deviceMacAddr] = data;
            if (!deviceSn) return;
            newArr.push({
              serialNumber: Number(deviceSn).toString(),
              imei: deviceImei ?? "",
              mac: deviceMacAddr ?? "",
            });
          });

          if (newArr.length === 0) {
            showError("CSV rows are missing serial numbers. Check column order: Serial Number, IMEI, MAC.");
            setIsUploadDisabled(false);
            return;
          }

          postInventory(newArr);
          setIsUploadDisabled(false);
        }}
      >
        {({ getRootProps, acceptedFile, ProgressBar }: any) => (
          <>
            <PageTitle>Inventory</PageTitle>

            <div className={style.barContainer}>
              <div className={style.filterContainer}>
                <TableFilterNew columns={filterColumns} onSearch={onSearch} />
              </div>
            </div>

            <div style={{ paddingBottom: "64px" }}>
              <CustomTable boxShadow>
                <CustomTable.Header columns={INVETNORY_COLUMNS} />

                <CustomTable.Body isLoading={isLoading} deviceData={deviceData}>
                  {deviceData &&
                    deviceData.map(
                      (row: InventoryValueTypes) =>
                        row && (
                          <TableRow key={row.serialNumber}>
                            {INVETNORY_COLUMNS.map((column) => {
                              const value: any = row[column.id as keyof InventoryValueTypes];

                              return <TableBodyCell key={column.id}>{value}</TableBodyCell>;
                            })}
                          </TableRow>
                        ),
                    )}
                </CustomTable.Body>
              </CustomTable>
            </div>

            <div className={style.importAreaContainer}>
              <div className={style.searchButtonMargin}>
                <PrimaryButton disabled={isUploadDisabled} {...getRootProps()}>
                  {ButtonText.CsvUpload}
                </PrimaryButton>
              </div>
              {getResultStats(acceptedFile)}
            </div>

            <ProgressBar class={style.progressBarColor} />
          </>
        )}
      </CSVReader>

      <TableContainer className={style.tableContainer} component={Paper}>
        <Table aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell className={style.tableHeaderCell} />
              <TableHeaderCell className={style.tableHeaderCell}>Serial Number</TableHeaderCell>
              <TableHeaderCell className={style.tableHeaderCell}>Upload Status</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {inventoryStatus
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row: InventoryStatusTypes) => (
                <TableRow key={row.serialNumber} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                  <TableCell className={style.tableHeaderCell} align="left">
                    {getStatusIcon(row.uploadStatus)}
                  </TableCell>
                  <TableCell className={style.tableHeaderCell} component="th" scope="row">
                    {row.serialNumber}
                  </TableCell>
                  <TableCell className={style.tableHeaderCell} component="th" scope="row">
                    {row.uploadStatus}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <div className={style.paginationContainer}>
          <TablePagination
            component="div"
            count={inventoryStatus.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </div>
      </TableContainer>

      <Popup title="Updating data..." open={isLoading}>
        <Spinner />
      </Popup>
    </>
  );
};
export default InventoryPage;
